import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

// =============================================================================
// BundleFreshness: the stale-tab cure (Jason 2026-06-12: "I loaded from an open
// webpage... not sure how we fix this whole stale thing"). An open tab keeps
// running the JS it loaded days ago, silently missing every deploy — fixes
// don't fix, the adoption pass never fires, bugs look unfixed.
//
// Every few minutes (and whenever the tab regains focus) this fetches the
// served index.html uncached, reads which hashed bundle the SERVER is shipping,
// and compares it to the bundle THIS tab is executing. Mismatch → a quiet pill:
// "New version — tap to refresh." Never auto-reloads (a live voice session
// must not be yanked); the user taps when ready. No-ops cleanly in dev (no
// hashed bundle to compare).
// =============================================================================

const CHECK_INTERVAL_MS = 4 * 60_000;
const BUNDLE_RE = /\/assets\/index-[\w-]+\.js/;

function currentBundle(): string | null {
  const script = document.querySelector<HTMLScriptElement>('script[src*="/assets/index-"]');
  const src = script?.getAttribute('src');
  return src ? (src.match(BUNDLE_RE)?.[0] ?? null) : null;
}

export function BundleFreshness() {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const mine = currentBundle();
    if (!mine) return; // dev server / unexpected markup: stay silent

    let disposed = false;
    const check = async () => {
      try {
        const res = await fetch('/', { cache: 'no-store' });
        if (!res.ok) return;
        const html = await res.text();
        const served = html.match(BUNDLE_RE)?.[0] ?? null;
        if (!disposed && served && served !== mine) setStale(true);
      } catch {
        /* offline or transient: try again next tick */
      }
    };

    const tick = setInterval(check, CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    check();
    return () => {
      disposed = true;
      clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (!stale) return null;

  return (
    <button
      type="button"
      onClick={() => window.location.reload()}
      className="fixed bottom-4 right-4 z-50 inline-flex items-center gap-2 rounded-full border border-brand-500 bg-white px-4 py-2 text-sm font-medium text-brand-700 shadow-card hover:bg-brand-50"
    >
      <RefreshCw className="h-4 w-4" />
      New version — tap to refresh
    </button>
  );
}
